import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CustomizedDataGrid from "./CustomizedDataGrid";
import { useState } from "react";
import { Alert, Chip, Divider, Snackbar, Stack } from "@mui/material";
import User from "../../../types/BackendUser";
import {
  addUser,
  deleteUser,
  toggleUserStatus,
  updateUser,
} from "../../../services/usersService";
import { FrontendUser } from "../../../types/FrontendUser";
import { useUsers } from "../../../hooks/useUsers";
import ConfirmationDialog from "../../shared/CustomConfirmationDialog";
import CustomUserDialog from "./customized-dialogs/CustomDialog";

export default function MainGrid() {
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<FrontendUser | null>(null);
  const [inviteSent, setInviteSent] = useState(false);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { loading, users, fetchUsers } = useUsers();
  const [confirmationMode, setConfirmationMode] = useState<"status" | "delete">(
    "status"
  );

  const handleEditClick = (user: any) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleAddClick = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleDeleteUserClick = (user: any) => {
    setConfirmationMode("delete");
    setConfirmationDialogOpen(true);
    setSelectedUser(user);
  };

  const handleToggleStatus = (user: FrontendUser) => {
    setConfirmationMode("status");
    setConfirmationDialogOpen(true);
    setSelectedUser(user);
  };

  const handleConfirmAction = async () => {
    if (!selectedUser) return;

    try {
      if (confirmationMode === "status") {
        await toggleUserStatus(selectedUser.email);
      } else if (confirmationMode === "delete") {
        await deleteUser(selectedUser.email);
      }
      await fetchUsers();
    } catch (err) {
      setErrorMessage(
        `Failed to ${confirmationMode === "delete" ? "delete" : "update"} user.`
      );
    } finally {
      setConfirmationDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleSaveUser = async (updatedUser: User) => {
    try {
      if (!editingUser) {
        // New user: send invitation
        const newUser = {
          username: updatedUser.username,
          groupName: updatedUser.groupName,
          givenName: updatedUser.givenName,
          familyName: updatedUser.familyName,
          employeeType: updatedUser.employeeType,
        };
        await addUser(newUser);
        setInviteSent(true);
      } else {
        await updateUser(updatedUser);
        await fetchUsers();
        if (
          updatedUser.previousUsername &&
          updatedUser.username !== updatedUser.previousUsername
        ) {
          setEmailChangeSuccess(true);
        }
      }
    } catch (error: any) {
      console.error("Error saving user:", error);
      setErrorMessage(error.response?.data?.error);
    } finally {
      setEditingUser(null);
      setDialogOpen(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 1, mt: 2.5 }}
      >
        <Box>
          <Typography
            component="h2"
            variant="overline"
            sx={{
              color: "text.secondary",
              letterSpacing: 0.8,
              fontWeight: 700,
            }}
          >
            Users
          </Typography>
        </Box>
        <Chip
          size="small"
          label={
            loading
              ? "Loading…"
              : `${users.length} ${users.length === 1 ? "User" : "Users"}`
          }
          variant="outlined"
          sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
        />
      </Stack>
      <Divider sx={{ mb: 2 }} />
      <Grid size={{ xs: 12, lg: 9 }}>
        <CustomizedDataGrid
          users={users}
          loading={loading}
          handleEditClick={handleEditClick}
          handleAddClick={handleAddClick}
          handleDeactivateUserClick={handleToggleStatus}
          handleDeleteUserClick={handleDeleteUserClick}
        />
      </Grid>

      {/* Add User or Edit User Dialog */}
      <CustomUserDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        user={editingUser}
        onSave={handleSaveUser}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => {
          setConfirmationDialogOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleConfirmAction}
        title={
          confirmationMode === "delete"
            ? "Delete User"
            : selectedUser?.status === "active"
            ? "Deactivate User"
            : "Reactivate User"
        }
        message={
          confirmationMode === "delete"
            ? "Are you sure you want to delete this user?"
            : `Are you sure you want to ${
                selectedUser?.status === "active" ? "deactivate" : "reactivate"
              } this user?`
        }
        confirmColor="secondary"
        confirmLabel="Confirm"
      />

      {/* Snackbar for invitation sent */}
      <Snackbar
        open={inviteSent}
        autoHideDuration={3000}
        onClose={() => setInviteSent(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setInviteSent(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Invitation sent successfully!
        </Alert>
      </Snackbar>

      {/* Snackbar for email change: verification sent */}
      <Snackbar
        open={emailChangeSuccess}
        autoHideDuration={5000}
        onClose={() => setEmailChangeSuccess(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setEmailChangeSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          User updated. A verification email has been sent to the new address.
        </Alert>
      </Snackbar>

      {/* Snackbar for error message */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
